<script lang="ts">
    import { getContext, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import type { Readable } from 'svelte/store'

    import type { GenOrder } from '../../order'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'

    import InputIntCurrency from 'hydria/components/InputIntCurrency.svelte'

    const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    let inputElement: HTMLInputElement | undefined = $state()

    let desc: string = $state('')
    let code: string = $state('')
    let listing: string = $state('')
    let qty: number = $state(1)
    let amount: number = $state(0)
    let withZone: boolean = $state(false)
    let actDomain: string = $state('')
    let actExpiration: string = $state('')
    let actExpirationDate: string = $state('') // Will swap to a "Date()" when selecting but we don't give a shit

    function reset() {
        desc = ''
        code = ''
        listing = ''
        qty = 1
        amount = 0
        withZone = false
        actDomain = ''
        actExpiration = ''
        actExpirationDate = ''
    }
    reset()

    let isValid = $derived.by(() => {
        let lvalid = true
        lvalid &&= !!code.match(/^[a-z][a-z0-9-]*$/)
        lvalid &&= !!desc
        lvalid &&= qty >= 1

        if (withZone) {
            lvalid &&= !!actDomain
            lvalid &&= !!actExpiration
        }

        return lvalid
    })

    onMount(() => {
        if (inputElement) inputElement.focus()
    })

    let adding: boolean = $state(false)
    async function submit() {
        adding = true
        let r: SimpleFetchResult

        const data = {
            code: code,
            desc: desc,
            qty: qty,
            value: amount,
            short: listing,
            is_pzone: withZone,
            pzone_a_name: withZone ? actDomain : '',
            pzone_expiration: withZone ? actExpiration : '',
        }
        try {
            r = await simpleFetch('PUT', `/json/ecom/1/order/${$orderId}/manual-item`, data)
        } catch (e: any) {
            r = e
            console.error('Failed to add custom:', e)
            let msg = `L’item personnalisé n’a pas pu être ajouté`
            notify({
                type: 'danger',
                title: 'Commande',
                text: msg + (r && r.data && r.data.error ? ` : ${r.data.error}` : '.'),
            })
        }
        if (r && r.data) {
            reset()
            if (inputElement) inputElement.focus()
            Order.loadGenericResponse(r.data)
        }
        adding = false
    }
</script>

<div id="order-transfer">
    <form
        class="d-grid gap-2"
        onsubmit={(e) => {
            e.preventDefault()
            submit()
        }}
    >
        <div class="row">
            <label for="cust-code" class="col-sm-3 col-form-label">Code</label>
            <div class="col-sm-9">
                <input id="cust-code" class="form-control" bind:value={code} bind:this={inputElement} disabled={adding} class:is-invalid={!code || !code.match(/^[a-z][a-z0-9-]*$/)} />
                {#if code && !code.match(/^[a-z][a-z0-9-]*$/)}
                    <div class="help-text text-danger">Permis: minuscule, nombres, et trait-d’union.</div>
                {/if}
            </div>
        </div>
        <div class="row">
            <label for="cust-desc" class="col-sm-3 col-form-label">Description</label>
            <div class="col-sm-9">
                <input type="text" id="cust-desc" class="form-control" bind:value={desc} disabled={adding} class:is-invalid={!desc} />
            </div>
        </div>
        <div class="row">
            <label for="cust-listing" class="col-sm-3 col-form-label">Description (Listing)</label>
            <div class="col-sm-9">
                <input type="text" id="cust-listing" class="form-control" bind:value={listing} disabled={adding} placeholder={desc} />
                <div class="help-text text-muted">Mettre un espace permet de ne rien afficher pour cette ligne dans le <em>listing</em>.</div>
            </div>
        </div>
        <div class="row">
            <label for="cust-qty" class="col-sm-3 col-form-label">Quantité</label>
            <div class="col-sm-9">
                <input type="number" min={0} id="cust-qty" class="form-control" bind:value={qty} disabled={adding} class:is-invalid={!qty} />
            </div>
        </div>
        <div class="row">
            <label for="cust-amount" class="col-sm-3 col-form-label">Montant</label>
            <div class="col-sm-9">
                <div class="input-group">
                    <InputIntCurrency class="form-control" bind:value={amount} min={null} max={null} id="cust-amount" disabled={adding} />
                    <span class="input-group-text">$</span>
                </div>
            </div>
        </div>

        <div class="row">
            <label for="cust-has-zone" class="col-sm-3 col-form-label">Activations</label>
            <div class="col-sm-9">
                <div class="checkbox"><label><input type="checkbox" bind:checked={withZone} disabled={adding} /> zone publiable</label></div>
            </div>
        </div>

        {#if withZone}
            <div class="row">
                <label for="cust-zone-name" class="col-sm-3 col-form-label">{$_('Domain')}</label>
                <div class="col-sm-9">
                    <input type="text" class="form-control" id="cust-zone-name" bind:value={actDomain} disabled={adding} class:is-invalid={!actDomain} />
                </div>
            </div>
            <div class="row">
                <label for="cust-zone-name" class="col-sm-3 col-form-label">{$_('Expiration')}</label>
                <div class="col-sm-9">
                    <Flatpickr options={{ element: '#edit-due' }} bind:formattedValue={actExpirationDate} bind:value={actExpiration} dateFormat="Y-m-d" element="#edit-due">
                        <div id="edit-due"><input type="text" class="form-control" data-input disabled={adding} class:is-invalid={!actDomain} /></div>
                    </Flatpickr>
                </div>
            </div>
        {/if}

        <div class="row">
            <div class="offset-sm-3 col-sm-9">
                <button type="submit" class="btn btn-outline-secondary" disabled={adding || !isValid}>
                    {#if adding}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        Un moment…
                    {:else}
                        {$_('Add')}
                    {/if}
                </button>
            </div>
        </div>
    </form>
</div>
